import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessGroups } from './ResourcesRouter.js';
import { connect } from 'react-redux';
import {
	Button,
	Table,
	TableHeader,
	TableBody,
	TableColumn,
	TableRow,
	TableCell,
	Select,
	SelectItem,
	Chip,
	Tooltip,
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Input,
	useDisclosure,
	Skeleton,
	Alert,
	addToast,
	Dropdown,
	DropdownTrigger,
	DropdownMenu,
	DropdownItem,
	SelectSection
} from '@heroui/react';
import {
	Home,
	Building2,
	Plane,
	Plus,
	Users,
	Eye,
	EyeOff,
	Edit,
	Settings,
	MapPin,
	Filter,
	Wrench,
	GraduationCap,
	Monitor,
	Package,
	X,
	Ban,
	TextSearch,
	Gauge,
	ChevronDown,
	ChevronRight,
	Lock,
	LayoutGrid
} from 'lucide-react';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import EditResourceModal from './EditResourceModal.js';
import MaintenanceModal from './MaintenanceModal.js';
import AddAircraftModal from './AddAircraftModal.js';
import AddInstructorModal from './AddInstructorModal.js';
import AddSimulatorModal from './AddSimulatorModal.js';
import ResourcePermissionsModal from './ResourcePermissionsModal.js';
import WhatsNewButton from '../common/WhatsNewDrawer/WhatsNewButton.js';
// ViewAircraftModal removed - using ViewAircraftPage instead
// ViewSimulatorModal removed - using ViewSimulatorPage instead
// InspectionsModal removed - using InspectionsPage instead
import ResourcesApi from '../../api/resourcesApi.js';
import {
	fetchResources,
	addResource,
	updateResource,
	deleteResource,
	toggleResourceVisibility,
	blockResourceType,
	unblockResourceType,
	updateResourceTypeName,
	fetchMaintenance,
	addMaintenance,
	updateMaintenance,
	deleteMaintenance
} from './resources.actions.js';

// Get org unique from URL for navigation links
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Color schemes for different resource types
const TYPE_COLORS = {
	aircraft: {
		gradient: 'from-blue-100/80 via-blue-50/50 to-transparent',
		cardBg: 'from-white to-blue-50/30',
		icon: 'bg-blue-500/10 ring-blue-500/20 text-blue-600',
		text: 'text-blue-900',
		border: 'border-blue-100/50'
	},
	simulator: {
		gradient: 'from-violet-100/80 via-violet-50/50 to-transparent',
		cardBg: 'from-white to-violet-50/30',
		icon: 'bg-violet-500/10 ring-violet-500/20 text-violet-600',
		text: 'text-violet-900',
		border: 'border-violet-100/50'
	},
	instructor: {
		gradient: 'from-emerald-100/80 via-emerald-50/50 to-transparent',
		cardBg: 'from-white to-emerald-50/30',
		icon: 'bg-emerald-500/10 ring-emerald-500/20 text-emerald-600',
		text: 'text-emerald-900',
		border: 'border-emerald-100/50'
	},
	default: {
		gradient: 'from-slate-100/80 via-slate-50/50 to-transparent',
		cardBg: 'from-white to-slate-50/30',
		icon: 'bg-slate-500/10 ring-slate-500/20 text-slate-600',
		text: 'text-slate-900',
		border: 'border-slate-100/50'
	}
};

// Get color scheme for type
const getTypeColors = typeUnique => {
	if (typeUnique && TYPE_COLORS[typeUnique]) {
		return TYPE_COLORS[typeUnique];
	}
	return TYPE_COLORS.default;
};

// Derive typeUnique from typeName if not set
const deriveTypeUnique = (typeUnique, typeName) => {
	if (typeUnique) return typeUnique;
	if (!typeName) return null;
	const name = typeName.toLowerCase();
	if (name === 'aircraft' || name === 'aircrafts') return 'aircraft';
	if (name === 'simulator' || name === 'simulators') return 'simulator';
	if (name === 'instructor' || name === 'instructors') return 'instructor';
	return null;
};

// Icon mapping for resource types
const getResourceTypeIcon = (typeUnique, className = 'w-4 h-4') => {
	switch (typeUnique) {
		case 'aircraft':
			return <Plane className={className} />;
		case 'instructor':
			return <GraduationCap className={className} />;
		case 'simulator':
			return <Monitor className={className} />;
		default:
			return <Package className={className} />;
	}
};

// Format rate display
const formatRate = (rate, payStatus) => {
	if (!rate) return null;
	const statusLabel = payStatus === 1 ? 'hr' : payStatus === 2 ? 'day' : 'hr';
	return `$${rate}/${statusLabel}`;
};

// Resolve an aircraft/simulator registration (N-number). Primary is the
// flattened `regNumb` from the resources LIST payload (Factories\Resources →
// Resource::toArray()); the nested `aircraft`/`simulator` shapes are detail
// fallbacks. Returns '' when unavailable (renders an empty cell, no error).
const getRegistrationNumber = resource =>
	resource?.regNumb ||
	resource?.registrationNumber ||
	resource?.aircraft?.registrationNumber ||
	resource?.simulator?.registrationNumber ||
	'';

/**
 * Resource Group Section
 * Premium styled section header with gradient, icon badge, and collapsible content
 */
function ResourceGroupSection({
	group,
	isMinimized,
	onToggle,
	children,
	isEditing,
	editingTypeName,
	onEditingTypeNameChange,
	onSaveTypeName,
	onCancelEdit,
	onStartEdit,
	isTypeEditable,
	blockedTypes,
	availableToBlock,
	onBlockType,
	onUnblockType,
	canManage
}) {
	const colors = getTypeColors(group.typeUnique);

	return (
		<div className={`rounded-xl border ${colors.border} bg-gradient-to-br ${colors.cardBg} overflow-hidden`}>
			{/* Section Header - Gradient styled */}
			<div className={`bg-gradient-to-r ${colors.gradient} px-4 py-3 border-b ${colors.border}`}>
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
					{/* Left: Toggle + Icon Badge + Title */}
					<div className="flex items-center gap-3">
						<Button
							isIconOnly
							size="sm"
							variant="light"
							className="min-w-8 w-8 h-8"
							onPress={onToggle}
						>
							{isMinimized ? (
								<ChevronRight className="w-4 h-4" />
							) : (
								<ChevronDown className="w-4 h-4" />
							)}
						</Button>

						{/* Icon Badge with ring effect */}
						<div className={`p-2 rounded-lg ${colors.icon} ring-1`}>
							{getResourceTypeIcon(group.typeUnique, 'w-5 h-5')}
						</div>

						<div className="flex items-center gap-2">
							{isEditing ? (
								<Input
									size="sm"
									variant="bordered"
									value={editingTypeName}
									onValueChange={onEditingTypeNameChange}
									onKeyDown={e => {
										if (e.key === 'Enter') onSaveTypeName();
										else if (e.key === 'Escape') onCancelEdit();
									}}
									onBlur={onSaveTypeName}
									autoFocus
									className="w-40"
									classNames={{ inputWrapper: 'border border-default-300' }}
								/>
							) : isTypeEditable && canManage ? (
								<span
									className={`text-lg font-semibold ${colors.text} cursor-pointer hover:opacity-80 truncate min-w-0`}
									onClick={onStartEdit}
									title={group.typeName}
								>
									{group.typeName}
								</span>
							) : (
								<span className={`text-lg font-semibold ${colors.text} truncate min-w-0`} title={group.typeName}>
									{group.typeName}
								</span>
							)}
							{group.system !== undefined &&
								group.system !== null &&
								(group.system === 1 || group.system === '1' || group.system === true) && (
									<Tooltip content="System type - cannot be renamed">
										<Lock className="w-3.5 h-3.5 text-default-400" />
									</Tooltip>
								)}
							<Chip size="sm" variant="flat" className="bg-white/60">
								{group.resources.length}
							</Chip>
						</div>
					</div>

					{/* Right: Disallowed Types */}
					{group.typeId !== 'other' && (
						<div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
							<span className="text-sm text-default-500 flex items-center gap-1 flex-shrink-0">
								<Ban className="w-3 h-3" />
								Disallowed:
							</span>
							<div className="flex items-center gap-1 flex-nowrap sm:flex-wrap">
								{blockedTypes.map(bt => (
									<Chip
										key={bt.id}
										size="sm"
										variant="flat"
										color="danger"
										onClose={canManage ? () => onUnblockType(bt.id, bt.name) : undefined}
									>
										{bt.name}
									</Chip>
								))}
								{blockedTypes.length === 0 && availableToBlock.length === 0 && (
									<span className="text-sm text-default-400">None</span>
								)}
							</div>
							{canManage && availableToBlock.length > 0 && (
								<Dropdown>
									<DropdownTrigger>
										<Button
											isIconOnly
											size="sm"
											variant="flat"
											className="min-w-6 w-6 h-6 bg-white/60 flex-shrink-0"
										>
											<Plus className="w-3 h-3" />
										</Button>
									</DropdownTrigger>
									<DropdownMenu
										aria-label="Block type"
										onAction={key => onBlockType(key)}
									>
										{availableToBlock.map(type => (
											<DropdownItem key={type.id}>{type.name}</DropdownItem>
										))}
									</DropdownMenu>
								</Dropdown>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Content - Resources Table */}
			{!isMinimized && (
				<div className="p-4 overflow-x-auto">
					{children}
				</div>
			)}
		</div>
	);
}

function ResourcesPage({
	// Redux state
	resources,
	resourcesTypes,
	locations,
	payStatuses,
	users,
	loading,
	saving,
	error,
	// Aircraft form data
	pilotRatings,
	instructorRatings,
	medicalClass,
	// Maintenance state
	maintenance,
	maintenanceLoading,
	// Redux dispatch
	dispatch
}) {
	const navigate = useNavigate();
	const accessGroups = useAccessGroups();
	const orgUnique = getOrgUnique();
	const [selectedLocations, setSelectedLocations] = React.useState(new Set(['all']));
	const [selectedType, setSelectedType] = React.useState('all');

	// Load minimized types from localStorage
	const [minimizedTypes, setMinimizedTypes] = React.useState(() => {
		try {
			const saved = localStorage.getItem('resources_minimized_types');
			if (saved) {
				return new Set(JSON.parse(saved));
			}
		} catch (e) {
			// Ignore localStorage errors
		}
		return new Set();
	});

	// Save minimized types to localStorage when changed
	React.useEffect(() => {
		try {
			localStorage.setItem('resources_minimized_types', JSON.stringify([...minimizedTypes]));
		} catch (e) {
			// Ignore localStorage errors
		}
	}, [minimizedTypes]);

	// Fetch fresh data from API on mount
	React.useEffect(() => {
		dispatch(fetchResources());
	}, [dispatch]);

	// Toggle type section expanded/collapsed
	const toggleTypeMinimized = typeId => {
		setMinimizedTypes(prev => {
			const next = new Set(prev);
			if (next.has(typeId)) {
				next.delete(typeId);
			} else {
				next.add(typeId);
			}
			return next;
		});
	};

	// Handle location selection change with mutual exclusivity logic
	const handleLocationSelectionChange = keys => {
		const newSelection = new Set(keys);

		// If nothing selected, default to "all"
		if (newSelection.size === 0) {
			setSelectedLocations(new Set(['all']));
			return;
		}

		const hadAll = selectedLocations.has('all');
		const hasAll = newSelection.has('all');

		// If "all" was just selected, clear all specific locations
		if (hasAll && !hadAll) {
			setSelectedLocations(new Set(['all']));
			return;
		}

		// If specific location selected while "all" is present, remove "all"
		if (hasAll && newSelection.size > 1) {
			newSelection.delete('all');
		}

		setSelectedLocations(newSelection);
	};
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [newResourceType, setNewResourceType] = React.useState('');
	const [isNewType, setIsNewType] = React.useState(false);
	const [newTypeName, setNewTypeName] = React.useState('');
	const [sortDescriptor, setSortDescriptor] = React.useState({
		column: 'name',
		direction: 'ascending'
	});

	// State for inline type name editing
	const [editingTypeId, setEditingTypeId] = React.useState(null);
	const [editingTypeName, setEditingTypeName] = React.useState('');

	// Form state for Add Resource modal
	const [formData, setFormData] = React.useState({
		name: '',
		locationID: '',
		rate: ''
	});
	const [formErrors, setFormErrors] = React.useState({});

	// Edit resource modal state
	const editModal = useDisclosure();
	const [editingResource, setEditingResource] = React.useState(null);

	// Maintenance modal state
	const maintenanceModal = useDisclosure();
	const [maintenanceResource, setMaintenanceResource] = React.useState(null);

	// Add Aircraft modal state
	const aircraftModal = useDisclosure();

	// Add Instructor modal state
	const instructorModal = useDisclosure();

	// Add Simulator modal state
	const simulatorModal = useDisclosure();

	// Resource Permissions modal state
	const permissionsModal = useDisclosure();
	const [permissionsResource, setPermissionsResource] = React.useState(null);

	// Inspections modal state removed - using InspectionsPage instead

	// View Simulator modal state removed - using ViewSimulatorPage instead

	// Helper to get location ID from resource (handles both SSR and API format)
	const getLocationId = resource => {
		if (resource.locationID) return resource.locationID;
		if (resource.location) {
			return typeof resource.location === 'object' ? resource.location.id : resource.location;
		}
		return null;
	};

	// Helper to get location name from resource
	const getLocationName = resource => {
		if (resource.locationName) return resource.locationName;
		if (resource.location && typeof resource.location === 'object') {
			return resource.location.name;
		}
		return '';
	};

	// Helper to check if a resource type is editable
	// System types (aircraft, simulator, instructor) cannot be edited
	// Types with undefined system are user-created and editable
	const isTypeEditable = typeId => {
		const type = resourcesTypes?.find(t => String(t.id) === String(typeId));
		// If type not found in resourcesTypes, it's likely a user-created type - allow editing
		if (!type) return true;

		// System types are never editable
		// undefined/null system means user-created type (editable)
		if (type.system === undefined || type.system === null) return true;
		if (type.system === 1 || type.system === '1' || type.system === true) return false;

		// Non-system types are editable
		return true;
	};

	// Filter resources by location
	const filteredResources = React.useMemo(() => {
		if (!resources) return [];
		return resources.filter(resource => {
			if (selectedLocations.has('all')) return true;
			const locId = getLocationId(resource);
			return selectedLocations.has(String(locId));
		});
	}, [resources, selectedLocations]);

	// Group resources by type and apply sorting
	const groupedResources = React.useMemo(() => {
		const groups = {};

		// Group resources by their type
		filteredResources.forEach(resource => {
			// Handle both SSR format (typeID) and API format (type which may be object or ID)
			let typeId, typeName, typeUnique;

			if (resource.typeID) {
				// SSR format
				typeId = resource.typeID;
				typeName = resource.typeName;
				typeUnique = resource.typeUnique;
			} else if (resource.type) {
				// API format - type might be object {id, name, unique} or just ID
				if (typeof resource.type === 'object') {
					typeId = resource.type.id;
					typeName = resource.type.name;
					typeUnique = resource.type.unique || resource.type.systemUnique;
				} else {
					typeId = resource.type;
					typeName = null;
					typeUnique = null;
				}
			} else {
				typeId = 'other';
				typeName = 'Other';
				typeUnique = null;
			}

			if (!groups[typeId]) {
				const type = resourcesTypes?.find(t => String(t.id) === String(typeId));
				const resolvedTypeName = typeName || type?.name || 'Other';
				groups[typeId] = {
					typeId,
					typeName: resolvedTypeName,
					typeUnique: deriveTypeUnique(typeUnique, resolvedTypeName),
					system: type?.system,
					my: type?.my,
					resources: []
				};
			}
			groups[typeId].resources.push(resource);
		});

		// Add empty groups for types that have no resources
		if (resourcesTypes && resourcesTypes.length > 0) {
			resourcesTypes.forEach(type => {
				const typeIdStr = String(type.id);
				// Check if this type already has a group (compare as strings)
				const existingKey = Object.keys(groups).find(key => String(key) === typeIdStr);
				if (!existingKey) {
					groups[typeIdStr] = {
						typeId: typeIdStr,
						typeName: type.name,
						typeUnique: deriveTypeUnique(type.systemUnique || type.unique, type.name),
						system: type.system,
						my: type.my,
						resources: []
					};
				}
			});
		}

		// Sort resources within each group based on sortDescriptor
		const sortedGroups = Object.values(groups);
		sortedGroups.forEach(group => {
			group.resources.sort((a, b) => {
				const { column, direction } = sortDescriptor;
				let comparison = 0;

				if (column === 'rate') {
					// Numeric comparison for rate
					const aRate = a.rate || 0;
					const bRate = b.rate || 0;
					comparison = aRate - bRate;
				} else if (column === 'locationName') {
					// String comparison for location
					const aLoc = getLocationName(a);
					const bLoc = getLocationName(b);
					comparison = aLoc.localeCompare(bLoc);
				} else if (column === 'regNumber') {
					// `sortDescriptor` is SHARED across every group table, but only
					// aircraft/simulator groups render a Reg. Number column. For any
					// other group, fall back to name sort so it never references a
					// column it doesn't have.
					const groupHasReg =
						group.typeUnique === 'aircraft' || group.typeUnique === 'simulator';
					if (groupHasReg) {
						comparison = getRegistrationNumber(a).localeCompare(
							getRegistrationNumber(b)
						);
					} else {
						comparison = (a.name || '').localeCompare(b.name || '');
					}
				} else {
					// String comparison for name (default)
					const aName = a.name || '';
					const bName = b.name || '';
					comparison = aName.localeCompare(bName);
				}

				return direction === 'descending' ? -comparison : comparison;
			});
		});

		return sortedGroups;
	}, [filteredResources, sortDescriptor, resourcesTypes]);

	// Location options for filter
	const locationOptions = React.useMemo(() => {
		const options = [{ id: 'all', name: 'All locations' }];
		if (locations) {
			locations.forEach(loc => {
				options.push({ id: String(loc.id), name: loc.name });
			});
		}
		return options;
	}, [locations]);

	// Resource type options for add form - grouped by ownership
	const typeOptionsGrouped = React.useMemo(() => {
		if (!resourcesTypes) return { nonOwner: [], owner: [] };

		// System types: system field is explicitly truthy (1, '1', true)
		// undefined means not a system type (user-created type)
		const isSystemType = type => {
			if (type.system === undefined || type.system === null) return false;
			return type.system === 1 || type.system === '1' || type.system === true;
		};

		// Non-Owner: system types only
		// Owner: non-system types (including undefined system which means user type)
		const nonOwner = resourcesTypes.filter(type => isSystemType(type));
		const owner = resourcesTypes.filter(type => !isSystemType(type));

		return { nonOwner, owner };
	}, [resourcesTypes]);

	// Set default resource type when typeOptionsGrouped changes
	// Also reset invalid selections that don't exist in available options
	React.useEffect(() => {
		const allTypes = [...typeOptionsGrouped.nonOwner, ...typeOptionsGrouped.owner];
		const isValidSelection =
			newResourceType && allTypes.some(t => String(t.unique || t.id) === String(newResourceType));

		if (!isValidSelection && !isNewType) {
			// Set to first nonOwner type (Aircraft), or first owner type if no nonOwner types
			const defaultType = typeOptionsGrouped.nonOwner[0] || typeOptionsGrouped.owner[0];
			if (defaultType) {
				setNewResourceType(String(defaultType.unique || defaultType.id));
			}
		}
	}, [typeOptionsGrouped.owner, typeOptionsGrouped.nonOwner, newResourceType, isNewType]);

	// Helper to get system type unique based on name
	const getSystemTypeUnique = typeName => {
		const name = (typeName || '').toLowerCase();
		if (name === 'aircraft') return 'aircraft';
		if (name === 'simulator' || name === 'simulators') return 'simulator';
		if (name === 'instructor' || name === 'instructors') return 'instructor';
		return null;
	};

	// Get selected type info for button text and behavior
	const selectedTypeInfo = React.useMemo(() => {
		if (isNewType) {
			return { name: 'Resource', unique: null, isSystemType: false };
		}
		// Check if selected type is in nonOwner (system types)
		const nonOwnerType = typeOptionsGrouped.nonOwner.find(
			t => String(t.unique || t.id) === String(newResourceType)
		);
		if (nonOwnerType) {
			// Get unique from type properties or derive from name
			const unique = nonOwnerType.unique || nonOwnerType.systemUnique || getSystemTypeUnique(nonOwnerType.name);
			return {
				name: nonOwnerType.name,
				unique,
				isSystemType: true
			};
		}
		// For owner types, always use "Resource"
		return { name: 'Resource', unique: null, isSystemType: false };
	}, [newResourceType, isNewType, typeOptionsGrouped]);

	// Handle Add button click - open appropriate modal based on type
	const handleAddButtonClick = () => {
		const typeUnique = selectedTypeInfo.unique;
		if (typeUnique === 'aircraft') {
			aircraftModal.onOpen();
		} else if (typeUnique === 'simulator') {
			simulatorModal.onOpen();
		} else if (typeUnique === 'instructor') {
			instructorModal.onOpen();
		} else {
			onOpen();
		}
	};

	// Handle aircraft added successfully
	const handleAircraftAdded = data => {
		// Refresh resources list
		dispatch(fetchResources());
	};

	// Handle simulator added successfully
	const handleSimulatorAdded = data => {
		// Refresh resources list
		dispatch(fetchResources());
	};

	// Handle instructor added successfully
	const handleInstructorAdded = data => {
		// Refresh resources list
		dispatch(fetchResources());
	};

	// Inspections modal handlers removed - using InspectionsPage instead
	// ViewSimulator modal handlers removed - using ViewSimulatorPage instead

	// Handle type selection change
	const handleTypeChange = keys => {
		const selectedKey = Array.from(keys)[0];
		if (selectedKey === '__new__') {
			setIsNewType(true);
			setNewResourceType('');
		} else {
			setIsNewType(false);
			setNewResourceType(selectedKey);
		}
	};

	// Navigation handlers
	const handleViewPermissions = resource => {
		setPermissionsResource(resource);
		permissionsModal.onOpen();
	};

	const handleViewResource = resource => {
		// API returns type.name, SSR returns typeUnique - normalize to lowercase
		const typeName = (resource.typeUnique || resource.type?.name || '').toLowerCase();

		if (typeName === 'aircraft') {
			// Navigate to View Aircraft Page using React Router
			const aircraftId = resource.aircraftID || resource.aircraft?.id;
			if (aircraftId) {
				navigate(`/aircraft/${aircraftId}`);
			}
		} else if (typeName === 'simulator') {
			// Navigate to View Simulator Page using React Router
			const simulatorId = resource.simulatorID || resource.simulator?.id;
			if (simulatorId) {
				navigate(`/simulator/${simulatorId}`);
			}
		} else {
			// Non-aircraft/simulator - open edit modal
			handleOpenEditModal(resource);
		}
	};

	const handleEditResource = resource => {
		// API returns type.name, SSR returns typeUnique - normalize to lowercase
		const typeName = (resource.typeUnique || resource.type?.name || '').toLowerCase();
		if (typeName === 'aircraft' || typeName === 'simulator') {
			handleViewResource(resource);
		} else {
			window.location.href = `/org/${orgUnique}/resources/#?model=resource&action=edit&element=${resource.id}`;
		}
	};

	// Open edit modal for non-aircraft/simulator resources
	const handleOpenEditModal = resource => {
		setEditingResource(resource);
		editModal.onOpen();
	};

	// Save resource changes
	const handleSaveResource = async (resourceId, data) => {
		try {
			await dispatch(updateResource(resourceId, data));
			editModal.onClose();
			setEditingResource(null);
		} catch (err) {
			throw err;
		}
	};

	// Delete resource
	const handleDeleteResourceFromModal = async resourceId => {
		try {
			await dispatch(deleteResource(resourceId));
			editModal.onClose();
			setEditingResource(null);
		} catch (err) {
			throw err;
		}
	};

	// Helper to determine View/Edit button configuration based on resource type
	const getViewEditConfig = resource => {
		// Get typeUnique from resource (SSR format) or from type object (API format)
		let typeUnique =
			resource.typeUnique ||
			(typeof resource.type === 'object'
				? resource.type?.unique || resource.type?.systemUnique
				: null);

		// Fallback: check type.name for 'Aircraft' or 'Simulator' if typeUnique is not set
		if (!typeUnique && typeof resource.type === 'object' && resource.type?.name) {
			const typeName = resource.type.name.toLowerCase();
			if (typeName === 'aircraft') typeUnique = 'aircraft';
			else if (typeName === 'simulator') typeUnique = 'simulator';
		}

		// Also check typeName directly (SSR format)
		if (!typeUnique && resource.typeName) {
			const typeName = resource.typeName.toLowerCase();
			if (typeName === 'aircraft') typeUnique = 'aircraft';
			else if (typeName === 'simulator' || typeName === 'simulators') typeUnique = 'simulator';
		}

		if (typeUnique === 'aircraft') {
			return {
				tooltip: 'View Aircraft',
				Icon: TextSearch,
				handler: () => handleViewResource(resource),
				requiresManage: false
			};
		}
		if (typeUnique === 'simulator') {
			return {
				tooltip: 'View Simulator',
				Icon: TextSearch,
				handler: () => handleViewResource(resource),
				requiresManage: false
			};
		}
		return { tooltip: 'Edit Resource', Icon: Edit, handler: () => handleOpenEditModal(resource), requiresManage: true };
	};

	const handleMaintenance = resourceId => {
		window.location.href = `/org/${orgUnique}/resources/#?model=maintenance&element=${resourceId}`;
	};

	// Open maintenance modal
	const handleOpenMaintenanceModal = resource => {
		setMaintenanceResource(resource);
		maintenanceModal.onOpen();
	};

	// Fetch maintenance data
	const handleFetchMaintenance = async () => {
		if (maintenanceResource?.id) {
			await dispatch(fetchMaintenance(maintenanceResource.id));
		}
	};

	// Add maintenance event
	const handleAddMaintenance = async data => {
		try {
			await dispatch(addMaintenance(maintenanceResource.id, data));
		} catch (err) {
			throw err;
		}
	};

	// Update maintenance event
	const handleSaveMaintenance = async (eventId, data) => {
		try {
			await dispatch(updateMaintenance(maintenanceResource.id, eventId, data));
		} catch (err) {
			throw err;
		}
	};

	// Delete maintenance event
	const handleDeleteMaintenance = async eventId => {
		try {
			await dispatch(deleteMaintenance(maintenanceResource.id, eventId));
		} catch (err) {
			throw err;
		}
	};

	// View meters/counter history
	const handleViewMetersHistory = resource => {
		// API returns type.name, SSR returns typeUnique - normalize to lowercase
		const typeName = (resource.typeUnique || resource.type?.name || '').toLowerCase();
		const aircraftId = resource.aircraftID || resource.aircraft?.id;
		const simulatorId = resource.simulatorID || resource.simulator?.id;

		if (typeName === 'aircraft' && aircraftId) {
			navigate(`/aircraft/${aircraftId}/meters`);
		} else if (typeName === 'simulator' && simulatorId) {
			navigate(`/simulator/${simulatorId}/meters`);
		} else {
			// Generic resource meters page (instructors, rooms, equipment, etc.)
			navigate(`/resource/${resource.id}/meters`);
		}
	};

	// Toggle visibility handler
	const handleToggleVisibility = async resource => {
		try {
			await dispatch(toggleResourceVisibility(resource.id, resource.published));
			const newPublished = !resource.published;
			addToast({
				title: 'Success',
				description: `Resource ${newPublished ? 'published' : 'unpublished'} successfully`,
				color: 'success'
			});
		} catch (err) {
			addToast({
				title: 'Error',
				description: err.response?.data?.message || 'Failed to update visibility',
				color: 'danger'
			});
		}
	};

	// Block resource type handler
	const handleBlockType = async (typeId, blockedType) => {
		try {
			const response = await dispatch(
				blockResourceType(typeId, blockedType, resourcesTypes)
			);
			// Handle API response messages
			const { info = [], error = [], warning = [] } = response?.data || {};
			if (info.length > 0) {
				info.forEach(msg => addToast({ title: 'Success', description: msg, color: 'success' }));
			}
			if (error.length > 0) {
				error.forEach(msg => addToast({ title: 'Error', description: msg, color: 'danger' }));
			}
			if (warning.length > 0) {
				warning.forEach(msg => addToast({ title: 'Warning', description: msg, color: 'warning' }));
			}
			if (info.length === 0 && error.length === 0) {
				addToast({
					title: 'Success',
					description: `Type "${blockedType.name}" blocked successfully`,
					color: 'success'
				});
			}
		} catch (err) {
			const errorData = err.response?.data || {};
			const errors = errorData.error || [];
			if (errors.length > 0) {
				errors.forEach(msg => addToast({ title: 'Error', description: msg, color: 'danger' }));
			} else {
				addToast({
					title: 'Error',
					description: err.message || 'Failed to block type',
					color: 'danger'
				});
			}
		}
	};

	// Unblock resource type handler
	const handleUnblockType = async (typeId, blockedTypeId, blockedTypeName) => {
		try {
			const response = await dispatch(
				unblockResourceType(typeId, blockedTypeId, resourcesTypes)
			);
			// Handle API response messages
			const { info = [], error = [], warning = [] } = response?.data || {};
			if (info.length > 0) {
				info.forEach(msg => addToast({ title: 'Success', description: msg, color: 'success' }));
			}
			if (error.length > 0) {
				error.forEach(msg => addToast({ title: 'Error', description: msg, color: 'danger' }));
			}
			if (warning.length > 0) {
				warning.forEach(msg => addToast({ title: 'Warning', description: msg, color: 'warning' }));
			}
			if (info.length === 0 && error.length === 0) {
				addToast({
					title: 'Success',
					description: `Type "${blockedTypeName}" unblocked successfully`,
					color: 'success'
				});
			}
		} catch (err) {
			const errorData = err.response?.data || {};
			const errors = errorData.error || [];
			if (errors.length > 0) {
				errors.forEach(msg => addToast({ title: 'Error', description: msg, color: 'danger' }));
			} else {
				addToast({
					title: 'Error',
					description: err.message || 'Failed to unblock type',
					color: 'danger'
				});
			}
		}
	};

	// Get blocked types for a given type
	// disallowedTypes comes from resourcesTypes[].disallowedTypes (API format)
	const getBlockedTypesForType = typeId => {
		const key = String(typeId);

		// Get from resourcesTypes[].disallowedTypes (API format)
		const type = resourcesTypes.find(t => String(t.id) === key);
		if (type && type.disallowedTypes) {
			return type.disallowedTypes.filter(t => t && t.id && t.name);
		}

		return [];
	};

	// Get available types to block (filter out same type and already blocked)
	// Matches legacy checkTypeForBlocking — no system type filtering
	const getAvailableTypesToBlock = typeId => {
		const blockedTypes = getBlockedTypesForType(typeId);
		const blockedIds = blockedTypes.map(t => String(t.id));
		return resourcesTypes.filter(t => {
			const typeIdStr = String(t.id);
			return typeIdStr !== String(typeId) && !blockedIds.includes(typeIdStr);
		});
	};

	// Save edited type name
	const handleSaveTypeName = async typeId => {
		const trimmedName = editingTypeName.trim();
		const group = groupedResources.find(g => String(g.typeId) === String(typeId));

		// Don't save if empty or unchanged
		if (!trimmedName || trimmedName === group?.typeName) {
			setEditingTypeId(null);
			setEditingTypeName('');
			return;
		}

		try {
			await dispatch(updateResourceTypeName(typeId, trimmedName, resourcesTypes));
			addToast({
				title: 'Success',
				description: `Type renamed to "${trimmedName}"`,
				color: 'success'
			});
		} catch (err) {
			addToast({
				title: 'Error',
				description: err.response?.data?.message || 'Failed to update type name',
				color: 'danger'
			});
		}

		setEditingTypeId(null);
		setEditingTypeName('');
	};

	// Form validation
	const validateForm = () => {
		const errors = {};
		if (!formData.name.trim()) {
			errors.name = 'Resource name is required';
		}
		if (!formData.locationID) {
			errors.locationID = 'Location is required';
		}
		if (isNewType && newTypeName.trim().length < 3) {
			errors.newTypeName = 'Type name must be at least 3 characters';
		}
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// Handle form input changes
	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		if (formErrors[field]) {
			setFormErrors(prev => ({ ...prev, [field]: null }));
		}
	};

	// Handle form submission
	const handleAddResource = async () => {
		if (!validateForm()) return;

		// Find the selected type's numeric ID (search in both owner and nonOwner arrays)
		const selectedType =
			typeOptionsGrouped.owner.find(t => String(t.unique || t.id) === String(newResourceType)) ||
			typeOptionsGrouped.nonOwner.find(t => String(t.unique || t.id) === String(newResourceType));

		const resourceData = {
			name: formData.name.trim(),
			locationId: parseInt(formData.locationID),
			rate: formData.rate || null,
			taxable: false,
			payStatus: formData.rate ? 'perhour' : null,
			...(isNewType ? { newType: newTypeName.trim() } : { typeId: selectedType?.id })
		};

		try {
			await dispatch(addResource(resourceData));
			addToast({
				title: 'Success',
				description: 'Resource added successfully',
				color: 'success'
			});
			// Reset form and close modal
			setFormData({ name: '', locationID: '', rate: '' });
			setFormErrors({});
			setIsNewType(false);
			setNewTypeName('');
			onClose();
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Failed to add resource';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		}
	};

	// Handle modal close - reset form
	const handleModalClose = () => {
		setFormData({ name: '', locationID: '', rate: '' });
		setFormErrors({});
		setIsNewType(false);
		setNewTypeName('');
		onClose();
	};

	// Error state
	if (error) {
		return (
			<div className="max-w-[1400px] mx-auto px-4 py-8">
				<HeroUIBreadcrumb currentPage="Resources" />
				<Alert color="danger" className="mt-4">
					Error loading resources: {error}
				</Alert>
			</div>
		);
	}

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			{/* Breadcrumbs */}
			<HeroUIBreadcrumb currentPage="Resources" />

			{/* Page Header - No Card wrapper, clean and open */}
			<div className="mt-6 mb-8">
				<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
					{/* Left: Title with icon badge */}
					<div className="flex items-center gap-3">
						<div className="p-2.5 rounded-xl bg-primary-500/10 ring-1 ring-primary-500/20">
							<LayoutGrid className="w-6 h-6 text-primary-600" />
						</div>
						<div>
							<div className="flex items-center gap-2">
								<h1 className="text-2xl font-bold text-default-900">Resources</h1>
								{/* What's New: backend merges global `general` + `resources`. */}
								<WhatsNewButton area="resources" />
							</div>
							<p className="text-sm text-default-500">Manage your organization's aircraft, simulators, and more</p>
						</div>
					</div>

					{/* Right: Quick Add controls (resourcesManage permission) */}
					{(accessGroups._loaded && accessGroups.resourcesManage) && (
						<div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 w-full sm:w-auto">
							<Select
								label="Resource Type"
								size="sm"
								selectedKeys={isNewType ? ['__new__'] : newResourceType ? [newResourceType] : []}
								onSelectionChange={handleTypeChange}
								className="w-full sm:w-44"
								aria-label="Resource type"
								variant="bordered"
								classNames={{ trigger: 'border border-default-300' }}
							>
								{typeOptionsGrouped.nonOwner.length > 0 && (
									<SelectSection
										title="Non-Owner"
										classNames={{ heading: 'text-default-400 text-xs' }}
									>
										{typeOptionsGrouped.nonOwner.map(type => (
											<SelectItem key={String(type.unique || type.id)}>{type.name}</SelectItem>
										))}
									</SelectSection>
								)}
								<SelectSection title="Owner" classNames={{ heading: 'text-default-400 text-xs' }}>
									<SelectItem key="__new__" startContent={<Plus className="w-4 h-4" />}>
										Create New Type
									</SelectItem>
									{typeOptionsGrouped.owner.map(type => (
										<SelectItem key={String(type.unique || type.id)}>{type.name}</SelectItem>
									))}
								</SelectSection>
							</Select>
							{isNewType && (
								<Input
									label="New Type Name"
									placeholder="Min 3 characters"
									value={newTypeName}
									onValueChange={setNewTypeName}
									size="sm"
									className="w-full sm:w-44"
									variant="bordered"
									classNames={{ inputWrapper: 'border border-default-300' }}
									isInvalid={!!formErrors.newTypeName}
									errorMessage={formErrors.newTypeName}
									isRequired
								/>
							)}
							<Button
								color="primary"
								startContent={<Plus className="w-4 h-4" />}
								onPress={handleAddButtonClick}
								className="shadow-sm"
							>
								Add {selectedTypeInfo.name}
							</Button>
						</div>
					)}
				</div>
			</div>

			{/* Filters Row - Standalone */}
			<div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-default-200">
				<div className="flex items-center gap-2 text-sm text-default-500">
					<span className="font-medium">Showing:</span>
					<Chip size="sm" variant="flat" color="primary">
						{selectedLocations.has('all') ? 'All Locations' : `${selectedLocations.size} Location(s)`}
					</Chip>
					<Chip size="sm" variant="flat">
						{groupedResources.reduce((sum, g) => sum + g.resources.length, 0)} Resources
					</Chip>
				</div>
				<Select
					label="Filter by Location"
					size="sm"
					selectionMode="multiple"
					selectedKeys={selectedLocations}
					onSelectionChange={handleLocationSelectionChange}
					className="w-full sm:w-48"
					startContent={<Filter className="w-4 h-4" />}
					aria-label="Filter by location"
					variant="bordered"
					classNames={{ trigger: 'border border-default-300' }}
				>
					{locationOptions.map(location => (
						<SelectItem key={location.id} value={location.id}>
							{location.name}
						</SelectItem>
					))}
				</Select>
			</div>

			{/* Resource Groups - Styled Sections */}
			{loading ? (
				<div className="space-y-4">
					{[1, 2, 3].map(i => (
						<Skeleton key={i} className="h-32 rounded-xl" />
					))}
				</div>
			) : groupedResources.length === 0 ? (
				<div className="text-center py-12 text-default-500">
					No resources found. Add your first resource to get started.
				</div>
			) : (
				<div className="space-y-4">
					{groupedResources.map(group => {
						// Reg. Number column is shown ONLY on aircraft & simulator
						// group tables (meaningless for instructors/rooms/equipment),
						// AND only once at least one resource actually has a
						// registration value. This keeps the column hidden while the
						// backend `aircraft/simulator.registrationNumber` field is not
						// yet live on an env (no visible-but-empty column), and it
						// self-activates when the field lands — no frontend redeploy.
						const showReg =
							(group.typeUnique === 'aircraft' ||
								group.typeUnique === 'simulator') &&
							group.resources.some(r => getRegistrationNumber(r));
						// `sortDescriptor` is a SINGLE shared state across every group
						// table. When a reg table is sorted by 'regNumber', non-reg
						// tables (which lack that column) would receive a descriptor
						// pointing at a column they don't have — React Aria then can't
						// resolve the node and crashes (`isRowHeader` of undefined).
						// Hand each non-reg table a name-sort descriptor instead.
						const effectiveSortDescriptor =
							!showReg && sortDescriptor.column === 'regNumber'
								? { column: 'name', direction: sortDescriptor.direction }
								: sortDescriptor;
						// Build the column set with the React Aria DYNAMIC collection
						// API. Conditional/`&&` children in <TableHeader>/<TableRow>
						// leave falsy nodes that desync React Aria's column↔cell index
						// mapping and crash on render (`isRowHeader` of undefined), so
						// the REG. NUMBER column must be added/removed at the data level.
						const columns = [
							{ key: 'name', label: 'NAME', allowsSorting: true, width: showReg ? '25%' : '30%' },
							...(showReg
								? [{ key: 'regNumber', label: 'REG. NUMBER', allowsSorting: true, width: '18%' }]
								: []),
							{ key: 'locationName', label: 'LOCATION', allowsSorting: true, width: showReg ? '27%' : '30%' },
							{ key: 'rate', label: 'RATE', allowsSorting: true, width: showReg ? '12%' : '15%' },
							{ key: 'actions', label: 'ACTIONS', align: 'end', width: showReg ? '18%' : '25%' }
						];
						// Cell content per column key — verbatim equivalents of the
						// previous static cells. Closes over `group`, `accessGroups`
						// and the existing row handlers.
						const renderResourceCell = (resource, columnKey) => {
							switch (columnKey) {
								case 'name':
									return (
										<div className="flex items-center gap-2">
											{getResourceTypeIcon(group.typeUnique, 'w-4 h-4 text-default-400 flex-shrink-0')}
											<span className="font-medium">{resource.name}</span>
										</div>
									);
								case 'regNumber':
									return (
										<span className="text-default-600 tabular-nums">
											{getRegistrationNumber(resource)}
										</span>
									);
								case 'locationName':
									return (
										<div className="flex items-center gap-1 text-default-600">
											<MapPin className="w-3 h-3 flex-shrink-0" />
											<span>{getLocationName(resource)}</span>
											{(resource.city || resource.location?.city) && (
												<span className="text-default-400 text-sm">
													({resource.city || resource.location?.city})
												</span>
											)}
										</div>
									);
								case 'rate':
									return !!resource.rate && resource.payStatus !== 'perhour' ? (
										<Chip color="primary" variant="flat" size="sm">
											{formatRate(resource.rate, resource.payStatus)}
										</Chip>
									) : null;
								case 'actions':
									return (
										<div className="flex gap-1 justify-end">
											{(accessGroups._loaded && accessGroups.resourcesManage) && (
												<>
													<Tooltip content="Resource Accessibility">
														<Button
															isIconOnly
															size="sm"
															variant="light"
															color="default"
															onPress={() => handleViewPermissions(resource)}
														>
															<Users className="w-4 h-4" />
														</Button>
													</Tooltip>
													<Tooltip
														content={
															resource.published
																? 'Published - Click to hide'
																: 'Hidden - Click to publish'
														}
													>
														<Button
															isIconOnly
															size="sm"
															variant="light"
															color={resource.published ? 'success' : 'default'}
															onPress={() => handleToggleVisibility(resource)}
														>
															{resource.published ? (
																<Eye className="w-4 h-4" />
															) : (
																<EyeOff className="w-4 h-4" />
															)}
														</Button>
													</Tooltip>
												</>
											)}
											{(() => {
												const { tooltip, Icon, handler, requiresManage } = getViewEditConfig(resource);
												if (requiresManage && !(accessGroups._loaded && accessGroups.resourcesManage)) return null;
												return (
													<Tooltip content={tooltip}>
														<Button
															isIconOnly
															size="sm"
															variant="light"
															color="primary"
															onPress={handler}
														>
															<Icon className="w-4 h-4" />
														</Button>
													</Tooltip>
												);
											})()}
											{(accessGroups._loaded && accessGroups.maintenanceManage) && (
												<Tooltip content="Maintenance">
													<Button
														isIconOnly
														size="sm"
														variant="light"
														color="warning"
														onPress={() => handleOpenMaintenanceModal(resource)}
													>
														<Wrench className="w-4 h-4" />
													</Button>
												</Tooltip>
											)}
											{(accessGroups._loaded && accessGroups.resourcesHistory) && (
												<Tooltip content="View Meters History">
													<Button
														isIconOnly
														size="sm"
														variant="light"
														color="secondary"
														onPress={() => handleViewMetersHistory(resource)}
													>
														<Gauge className="w-4 h-4" />
													</Button>
												</Tooltip>
											)}
										</div>
									);
								default:
									return null;
							}
						};
						return (
						<ResourceGroupSection
							key={group.typeId}
							group={group}
							isMinimized={minimizedTypes.has(group.typeId)}
							onToggle={() => toggleTypeMinimized(group.typeId)}
							isEditing={editingTypeId === group.typeId}
							editingTypeName={editingTypeName}
							onEditingTypeNameChange={setEditingTypeName}
							onSaveTypeName={() => handleSaveTypeName(group.typeId)}
							onCancelEdit={() => {
								setEditingTypeId(null);
								setEditingTypeName('');
							}}
							onStartEdit={() => {
								setEditingTypeId(group.typeId);
								setEditingTypeName(group.typeName);
							}}
							isTypeEditable={isTypeEditable(group.typeId)}
							blockedTypes={getBlockedTypesForType(group.typeId)}
							availableToBlock={getAvailableTypesToBlock(group.typeId)}
							onBlockType={key => {
								const typeToBlock = resourcesTypes.find(t => String(t.id) === String(key));
								if (typeToBlock) handleBlockType(group.typeId, typeToBlock);
							}}
							onUnblockType={(blockedTypeId, blockedTypeName) =>
								handleUnblockType(group.typeId, blockedTypeId, blockedTypeName)
							}
							canManage={accessGroups._loaded && !!accessGroups.resourcesManage}
						>
							<Table
								aria-label={`${group.typeName} resources`}
								removeWrapper
								className={showReg ? 'min-w-[680px]' : 'min-w-[600px]'}
								classNames={{
									th: 'bg-default-100 text-default-600 font-semibold text-xs uppercase tracking-wider py-3',
									td: 'py-3',
									tbody: '[&>tr:nth-child(even)>td]:bg-default-50 [&>tr:nth-child(even)>td:first-child]:rounded-l-lg [&>tr:nth-child(even)>td:last-child]:rounded-r-lg'
								}}
								sortDescriptor={effectiveSortDescriptor}
								onSortChange={setSortDescriptor}
							>
								<TableHeader columns={columns}>
									{col => (
										<TableColumn
											key={col.key}
											allowsSorting={col.allowsSorting}
											align={col.align}
											width={col.width}
											className={col.key === 'actions' ? 'text-right' : undefined}
										>
											{col.label}
										</TableColumn>
									)}
								</TableHeader>
								<TableBody items={group.resources}>
									{resource => (
										<TableRow key={resource.id} className="cursor-pointer hover:bg-default-100">
											{columnKey => <TableCell>{renderResourceCell(resource, columnKey)}</TableCell>}
										</TableRow>
									)}
								</TableBody>
							</Table>
						</ResourceGroupSection>
						);
					})}
				</div>
			)}

			{/* Add Resource Modal */}
			<Modal scrollBehavior="inside" isOpen={isOpen} onClose={handleModalClose} size="lg">
				<ModalContent>
					{onClose => (
						<>
							<ModalHeader className="flex flex-col gap-1">Add New {selectedTypeInfo.name}</ModalHeader>
							<ModalBody>
								<div className="space-y-4">
									<Input
										label="Resource Name"
										placeholder="Enter resource name"
										variant="bordered"
										value={formData.name}
										onValueChange={value => handleInputChange('name', value)}
										isInvalid={!!formErrors.name}
										errorMessage={formErrors.name}
										isRequired
									/>
									<Select
										label="Location"
										placeholder="Select location"
										variant="bordered"
										aria-label="Select location"
										selectedKeys={formData.locationID ? [formData.locationID] : []}
										onSelectionChange={keys => handleInputChange('locationID', Array.from(keys)[0])}
										isInvalid={!!formErrors.locationID}
										errorMessage={formErrors.locationID}
										isRequired
									>
										{locationOptions
											.filter(loc => loc.id !== 'all')
											.map(location => (
												<SelectItem key={location.id} value={location.id}>
													{location.name}
												</SelectItem>
											))}
									</Select>
									<Input
										label="Hourly Rate"
										placeholder="Enter hourly rate (e.g., 150)"
										variant="bordered"
										startContent="$"
										endContent="/hr"
										type="number"
										value={formData.rate}
										onValueChange={value => handleInputChange('rate', value)}
									/>
									<p className="text-sm text-default-400">
										Resource type:{' '}
										<span className="font-medium text-default-600">
											{isNewType
												? newTypeName || 'New Type'
												: (
														typeOptionsGrouped.owner.find(
															t => String(t.unique || t.id) === String(newResourceType)
														) ||
														typeOptionsGrouped.nonOwner.find(
															t => String(t.unique || t.id) === String(newResourceType)
														)
													)?.name || 'Unknown'}
										</span>
									</p>
								</div>
							</ModalBody>
							<ModalFooter>
								<Button color="danger" variant="light" onPress={handleModalClose}>
									Cancel
								</Button>
								<Button color="primary" onPress={handleAddResource} isLoading={saving}>
									Add {selectedTypeInfo.name}
								</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>

			{/* Edit Resource Modal */}
			<EditResourceModal
				isOpen={editModal.isOpen}
				onClose={() => {
					editModal.onClose();
					setEditingResource(null);
				}}
				resource={editingResource}
				locations={locations}
				payStatuses={payStatuses}
				onSave={handleSaveResource}
				onDelete={handleDeleteResourceFromModal}
				saving={saving}
			/>

			{/* Maintenance Modal */}
			<MaintenanceModal
				isOpen={maintenanceModal.isOpen}
				onClose={() => {
					maintenanceModal.onClose();
					setMaintenanceResource(null);
				}}
				resource={maintenanceResource}
				maintenanceData={maintenanceResource ? maintenance[maintenanceResource.id] : null}
				loading={maintenanceLoading}
				onFetch={handleFetchMaintenance}
				onAdd={handleAddMaintenance}
				onSave={handleSaveMaintenance}
				onDelete={handleDeleteMaintenance}
			/>

			{/* Add Aircraft Modal */}
			<AddAircraftModal
				isOpen={aircraftModal.isOpen}
				onClose={aircraftModal.onClose}
				locations={locations}
				payStatuses={payStatuses}
				onSuccess={handleAircraftAdded}
			/>

			{/* Add Instructor Modal */}
			<AddInstructorModal
				isOpen={instructorModal.isOpen}
				onClose={instructorModal.onClose}
				payStatuses={payStatuses}
				resources={resources}
				resourcesTypes={resourcesTypes}
				onSuccess={handleInstructorAdded}
			/>

			{/* Add Simulator Modal */}
			<AddSimulatorModal
				isOpen={simulatorModal.isOpen}
				onClose={simulatorModal.onClose}
				payStatuses={payStatuses}
				onSuccess={handleSimulatorAdded}
			/>

			{/* Resource Permissions Modal */}
			<ResourcePermissionsModal
				isOpen={permissionsModal.isOpen}
				onClose={() => {
					permissionsModal.onClose();
					setPermissionsResource(null);
				}}
				resource={permissionsResource}
				users={users}
			/>

			{/* InspectionsModal removed - using InspectionsPage instead */}
			{/* ViewSimulatorModal removed - using ViewSimulatorPage instead */}
		</div>
	);
}

// mapStateToProps - get data from Redux store
const mapStateToProps = state => ({
	resources: state.resources?.resources || [],
	resourcesTypes: state.resources?.resourcesTypes || [],
	locations: state.resources?.locations || [],
	payStatuses: state.resources?.payStatuses || [],
	users: state.resources?.users || [],
	loading: state.resources?.loading || false,
	saving: state.resources?.saving || false,
	error: state.resources?.error || null,
	// Aircraft form data
	pilotRatings: state.resources?.pilotRatings || [],
	instructorRatings: state.resources?.instructorRatings || [],
	medicalClass: state.resources?.medicalClass || [],
	// Maintenance state
	maintenance: state.resources?.maintenance || {},
	maintenanceLoading: state.resources?.maintenanceLoading || false
});

export default connect(mapStateToProps)(ResourcesPage);
